import { LitElement, PropertyValues, html } from 'lit';
import { customElement, query, property, state } from 'lit/decorators.js';
import { getGlobalStyleSheets } from '../../global-styles';

import { ExpandableTextTags } from './expandable-text.tagnames';
import { AtlExpandableTextProps } from './expandable-text.types';
import { AtlExpandableTextToggleEvent } from './expandable-text.events';

import ExpandableTextStyles from './expandable-text.scss?inline';

@customElement(ExpandableTextTags.ExpandableText)
export class AtlExpandableText extends LitElement implements AtlExpandableTextProps {
    @property({ type: Number, attribute: 'line-clamp' })
    lineClamp: number = 2;

    @state()
    private isExpanded: boolean = false;

    @state()
    private showToggle: boolean = false;

    @query('.expandable-text-content')
    private contentElement?: HTMLDivElement;
    private resizeObserver?: ResizeObserver;

    static styles = [...getGlobalStyleSheets(), ExpandableTextStyles];

    protected override firstUpdated(changedProperties: PropertyValues): void {
        super.firstUpdated(changedProperties);
        this.checkOverflow();
        this.setupResizeObserver();
    }

    override disconnectedCallback(): void {
        this.cleanupResizeObserver();
        super.disconnectedCallback();
    }

    private setupResizeObserver(): void {
        if (!this.contentElement) return;

        this.resizeObserver = new ResizeObserver(() => {
            this.checkOverflow();
        });

        this.resizeObserver.observe(this.contentElement);
    }

    private cleanupResizeObserver(): void {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = undefined;
        }
    }

    protected override updated(changedProperties: PropertyValues): void {
        super.updated(changedProperties);

        if (changedProperties.has('lineClamp')) {
            this.checkOverflow();
        }
    }

    private checkOverflow(): void {
        if (!this.contentElement) return;

        const hasOverflow = this.contentElement.scrollHeight > this.contentElement.clientHeight;
        this.showToggle = this.isExpanded || hasOverflow;
    }

    private async toggleExpanded(): Promise<void> {
        this.isExpanded = !this.isExpanded;

        this.dispatchEvent(
            new AtlExpandableTextToggleEvent({
                expanded: this.isExpanded,
            })
        );

        await this.updateComplete;
        this.checkOverflow();
    }

    render() {
        const contentClasses = this.isExpanded ? 'expanded' : 'collapsed';
        const toggleClasses = this.showToggle ? '' : 'hidden';

        return html`
         <div class="expandable-text-wrapper">
            <div 
               class="expandable-text-content ${contentClasses}"
               style="--line-clamp: ${this.lineClamp}"
            >
               <slot></slot>
            </div>
            <button
               class="toggle-button ${toggleClasses}"
               @click=${this.toggleExpanded}
               aria-expanded="${this.isExpanded}"
            >
               ${this.isExpanded ? 'See less' : 'See more'}
            </button>
         </div>
      `;
    }
}